import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchTrainings, fetchCourseRoster, createCourse, updateCourse, deleteCourse,
  markCompletion, createCredential, deleteCredential,
  fetchCourseTargets, addCourseTarget, removeCourseTarget,
  COURSE_CYCLES, COURSE_CYCLE_LABEL, CREDENTIAL_KIND_LABEL,
  type TrainingBoardDto, type CourseRosterDto, type CourseKind, type CourseCycle,
  type CredentialKind, type CredentialDto, type CourseTargetDto,
} from '../../api/hr-trainings';
import { listEmployees, type EmployeeRowDto } from '../../api/hr-employee';
import { listWorkSchedules } from '../../api/hr-schedule';
import './TrainingsPage.css';
// 기준 설정 (2026-08-28 · 판 7) — 종류의 «이름»과 «씀 여부»는 회사가 정한다.
//   못 읽으면 아래 기본 목록이 그대로 산다(깜빡이지 않는다).
import { useCompanyCodes } from '../../lib/company-codes';
// 채택 3단계 ② 인사 — 단추를 정본 Button 으로(D1).
import { Button } from '../../components/shared';
// 손짠 머리 → 정본 PageHeader · 경로 · 범위 칩(2026-10-17 · web 레인 W7a — lib/page-crumbs.ts)
import { PageHeader } from '../../components/shared';
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';

/**
 * 교육 · 자격 · 어학 — 담당자 화면 (시안 A22, migration 135).
 *
 * ★★ **세 탭을 한 화면으로 묶은 이유가 그대로 화면의 뼈대다** — 담당자에게 이 셋은 같은 일,
 *    «기한이 있는 것을 놓치지 않는 일» 이라 **법정교육 이수율과 만료 임박을 맨 위에** 세운다.
 * ★★ **만료 없음과 만료됨을 섞지 않는다.** 평생 유효한 자격은 「만료」 칸이 «—» 이고 임박
 *    목록에 오지 않는다 — 끼면 그 목록을 아무도 안 보게 된다.
 * ★ **이수 대상은 재직자 전원이다.** 과정별 대상 범위 지정이 아직 없다는 것을 화면이 말한다.
 * ★ **교육을 열어도 메일·알림은 안 나간다.** 시안 모달의 발송은 파이프라인이 없다 —
 *   기간·방식을 기록하는 데까지가 지금 하는 일이고, 화면이 그것을 먼저 말한다.
 */

type Tab = 'STATUTORY' | 'INTERNAL' | 'LICENSE' | 'LANGUAGE';

const TABS: ReadonlyArray<{ key: Tab; label: string }> = [
  { key: 'STATUTORY', label: '법정교육' },
  { key: 'LICENSE', label: '자격' },
  { key: 'LANGUAGE', label: '어학' },
  { key: 'INTERNAL', label: '사내교육' },
];

const STATE_LABEL: Record<string, string> = { DONE: '완료', OVERDUE: '기한 지남', OPEN: '진행 중' };
const EXPIRY_LABEL: Record<string, string> = { NONE: '만료 없음', VALID: '유효', SOON: '임박', EXPIRED: '지남' };

const emptyCourse = () => ({ name: '', cycle: 'ANNUAL' as CourseCycle, dueOn: '' });
const emptyCred = () => ({
  userId: '', kind: 'LICENSE' as CredentialKind, name: '', score: '',
  acquiredOn: '', expiresOn: '', hasProof: false,
});

/** D-84 · 지남 — 서버가 준 남은 날을 문구로만 바꾼다. */
function dueText(days: number | null): string {
  if (days === null) return '—';
  return days < 0 ? '지남' : `D-${days}`;
}

export function TrainingsPage() {
  // ★ 회사가 고친 이름을 얹는다. 갈래 넷 중 «자격·어학» 둘만 기준 설정에 있고,
  //   교육(법정·사내)은 목록에 없어 이름이 그대로 지나간다.
  const codes = useCompanyCodes('hr');
  const tabs = codes.apply('credential_kind', TABS);
  const [board, setBoard] = useState<TrainingBoardDto | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'folded'>('loading');
  const [tab, setTab] = useState<Tab>('STATUTORY');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const [employees, setEmployees] = useState<EmployeeRowDto[]>([]);
  const [courseForm, setCourseForm] = useState(emptyCourse);
  const [credForm, setCredForm] = useState(emptyCred);
  const [roster, setRoster] = useState<CourseRosterDto | null>(null);
  const [teams, setTeams] = useState<Array<{ id: string; name: string }>>([]);
  /** 대상 규칙을 열어 둔 과정 — 목록 옆이 아니라 그 과정 아래에서 편집한다. */
  const [targetsFor, setTargetsFor] = useState<{ courseId: string; name: string } | null>(null);
  const [targets, setTargets] = useState<CourseTargetDto[] | null>(null);
  const [pickKind, setPickKind] = useState<'TEAM' | 'USER'>('TEAM');
  const [pickId, setPickId] = useState('');

  const load = useCallback(async () => {
    setBoard(await fetchTrainings());
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await load();
        if (alive) setState('ok');
      } catch {
        if (alive) setState('folded');
        return;
      }
      try {
        const emp = await listEmployees();
        if (alive) setEmployees(emp.rows.filter((e) => e.employmentStatus !== 'RESIGNED'));
      } catch { /* 사람 고르기만 못 쓴다 */ }
      try {
        const sch = await listWorkSchedules();
        if (alive) setTeams(sch.teams);
      } catch { /* 팀 고르기만 못 쓴다 */ }
    })();
    return () => { alive = false; };
  }, [load]);

  const isCourseTab = tab === 'STATUTORY' || tab === 'INTERNAL';

  const courses = useMemo(
    () => (board?.courses ?? []).filter((c) => c.kind === tab),
    [board, tab],
  );

  const credentials = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (board?.credentials ?? [])
      .filter((c) => c.kind === tab)
      .filter((c) => !q
        || (c.userName ?? '').toLowerCase().includes(q)
        || (c.employeeNumber ?? '').toLowerCase().includes(q)
        || c.title.toLowerCase().includes(q));
  }, [board, tab, search]);

  const openRoster = async (courseId: string) => {
    setBusy(courseId);
    setError(null);
    try {
      setRoster(await fetchCourseRoster(courseId));
    } catch {
      setError('이수 현황을 불러오지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const toggleDone = async (courseId: string, userId: string, done: boolean) => {
    setBusy(`${courseId}:${userId}`);
    setError(null);
    try {
      await markCompletion(courseId, userId, done);
      await Promise.all([load(), fetchCourseRoster(courseId).then(setRoster)]);
    } catch {
      setError('이수를 기록하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const openTargets = async (courseId: string, name: string) => {
    setBusy(courseId);
    setError(null);
    try {
      const res = await fetchCourseTargets(courseId);
      setTargets(res.rows);
      setTargetsFor({ courseId, name });
      setPickId('');
    } catch {
      setError('대상을 불러오지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const addTarget = async () => {
    if (!targetsFor || !pickId) { setError(pickKind === 'TEAM' ? '팀을 고르세요' : '사람을 고르세요'); return; }
    setBusy('target');
    setError(null);
    try {
      await addCourseTarget(targetsFor.courseId, {
        kind: pickKind,
        ...(pickKind === 'TEAM' ? { teamId: pickId } : { userId: pickId }),
      });
      setPickId('');
      const res = await fetchCourseTargets(targetsFor.courseId);
      setTargets(res.rows);
      await load();
    } catch (e) {
      setError(msgOf(e) ?? '대상을 더하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const dropTarget = async (id: string) => {
    if (!targetsFor) return;
    setBusy(id);
    setError(null);
    try {
      await removeCourseTarget(id);
      const res = await fetchCourseTargets(targetsFor.courseId);
      setTargets(res.rows);
      await load();
    } catch {
      setError('대상을 빼지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const addCourse = async () => {
    if (!courseForm.name.trim()) { setError('교육 이름은 있어야 합니다'); return; }
    setBusy('course');
    setError(null);
    try {
      await createCourse({
        name: courseForm.name.trim(),
        kind: tab as CourseKind,
        cycle: courseForm.cycle,
        ...(courseForm.dueOn ? { dueOn: courseForm.dueOn } : {}),
      });
      setCourseForm(emptyCourse());
      await load();
    } catch (e) {
      setError(msgOf(e) ?? '교육을 열지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const archive = async (id: string, active: boolean) => {
    setBusy(id);
    setError(null);
    try {
      await updateCourse(id, { active });
      await load();
    } catch {
      setError('바꾸지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const removeCourse = async (id: string) => {
    setBusy(id);
    setError(null);
    try {
      await deleteCourse(id);
      if (roster?.course.id === id) setRoster(null);
      await load();
    } catch {
      setError('지우지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const addCred = async () => {
    if (!credForm.userId || !credForm.name.trim() || !credForm.acquiredOn) {
      setError('대상 · 이름 · 취득일은 있어야 합니다');
      return;
    }
    setBusy('cred');
    setError(null);
    try {
      await createCredential({
        userId: credForm.userId,
        kind: tab as CredentialKind,
        name: credForm.name.trim(),
        score: credForm.score.trim() || null,
        acquiredOn: credForm.acquiredOn,
        // ★ 비워 두면 «만료 없음» 이다 — 먼 날짜로 채우지 않는다.
        ...(credForm.expiresOn ? { expiresOn: credForm.expiresOn } : {}),
        hasProof: credForm.hasProof,
      });
      setCredForm(emptyCred());
      await load();
    } catch (e) {
      setError(msgOf(e) ?? '등록하지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const removeCred = async (c: CredentialDto) => {
    setBusy(c.id);
    setError(null);
    try {
      await deleteCredential(c.id);
      await load();
    } catch {
      setError('지우지 못했습니다');
    } finally {
      setBusy(null);
    }
  };

  const s = board?.summary;

  return (
    <div className="hrpage hrtr">
      <PageHeader
        breadcrumb={crumbsFor('/erp/hr/records/training')}
        scope={scopeFor('/erp/hr/records/training')}
        title="교육 · 자격 · 어학"
        description="기한이 있는 것을 놓치지 않습니다"
      />

      {state === 'folded' && (
        <div className="hrpage__note">교육 · 자격을 불러오지 못했습니다 — 서버가 아직 준비되지 않았을 수 있습니다.</div>
      )}

      {state !== 'folded' && (
        <>
          {error && <p className="hrcert__err" role="alert">{error}</p>}

          {s && (
            <div className="hrpage__kpis">
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">법정교육</span>
                <span className="hrpage__kpiv">{s.statutoryCourses}<small>종</small></span>
                <span className="hrpage__kpid">
                  {s.statutoryCourses > 0
                    ? `${s.statutoryDone}종 완료 · ${s.statutoryCourses - s.statutoryDone}종 진행 중`
                    : '아직 연 교육이 없습니다'}
                </span>
              </div>
              <div className="hrpage__kpi hrtr__kpi--warn">
                <span className="hrpage__kpil">미이수</span>
                <span className="hrpage__kpiv">{s.notCompleted}<small>명</small></span>
                <span className="hrpage__kpid">{s.worstCourse ?? '빠진 사람이 없습니다'}</span>
              </div>
              <div className="hrpage__kpi">
                <span className="hrpage__kpil">자격 · 어학 보유</span>
                <span className="hrpage__kpiv">{s.holders}<small>명</small></span>
                <span className="hrpage__kpid">총 {s.credentials}건</span>
              </div>
              <div className="hrpage__kpi hrtr__kpi--warn">
                <span className="hrpage__kpil">만료 임박</span>
                <span className="hrpage__kpiv">{s.expiringSoon}<small>건</small></span>
                <span className="hrpage__kpid">
                  {board?.windowDays ?? 90}일 안{s.expired > 0 ? ` · 이미 지남 ${s.expired}건` : ''}
                </span>
              </div>
            </div>
          )}

          {board && board.expiring.length > 0 && (
            <section className="hrpage__panel">
              <h2 className="hrpage__panelt">
                만료가 다가오는 것
                <span className="hrpage__panels">{board.windowDays}일 전부터 알립니다 — 지난 것이 맨 앞입니다</span>
              </h2>
              <ul className="hrtr__due">
                {board.expiring.map((c) => (
                  <li key={c.id} className={c.expiry === 'EXPIRED' ? 'is-past' : ''}>
                    <span className={`hrpage__pill hrtr__d${c.expiry === 'EXPIRED' ? ' hrtr__d--past' : ''}`}>
                      {dueText(c.daysLeft)}
                    </span>
                    <b>{c.userName ?? '—'}</b> · {c.title}
                    <span className="hrpage__dim">
                      {' '}{c.expiresOn} 만료
                      {c.expiry === 'EXPIRED' ? ' — 증명서에서 빠집니다' : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <div className="hrtr__bar">
            <div className="hrcert__tabs" role="group" aria-label="갈래">
              {tabs.map((t) => (
                <button
                  key={t.key} type="button"
                  className={`hrcert__tab${tab === t.key ? ' is-on' : ''}`}
                  onClick={() => { setTab(t.key); setRoster(null); setError(null); }}
                  aria-pressed={tab === t.key}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {!isCourseTab && (
              <input
                className="hrtr__search" value={search} aria-label="이름 · 사번으로 찾기"
                placeholder="이름 · 사번으로 찾기"
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
          </div>

          {isCourseTab ? (
            <>
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  {tab === 'STATUTORY' ? '법정 의무교육' : '사내교육'}
                  <span className="hrpage__panels">
                    {tab === 'STATUTORY' ? '미이수는 과태료 대상입니다 · ' : ''}
                    재직 {board?.headcount ?? 0}명 · 과정마다 대상을 좁힐 수 있습니다
                  </span>
                </h2>
                {courses.length === 0 ? (
                  <p className="hrpage__empty">아직 연 교육이 없습니다.</p>
                ) : (
                  <table className="hrpage__tbl">
                    <thead>
                      <tr>
                        <th>교육</th><th>주기</th><th>기한</th><th>대상</th>
                        <th className="hrpage__num">이수</th><th className="hrpage__num">이수율</th>
                        <th className="hrpage__act">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {courses.map((c) => (
                        <tr key={c.id} className={c.state === 'OVERDUE' ? 'is-overdue' : ''}>
                          <td>{c.name}</td>
                          <td className="hrpage__dim">{c.cycleLabel}</td>
                          <td className="hrtr__nowrap">
                            {c.dueOn ?? '—'}
                            {c.state === 'OVERDUE' && <span className="hrpage__pill hrtr__d--past">지남</span>}
                          </td>
                          <td className="hrtr__nowrap">
                            {c.targetScope === 'ALL'
                              ? <span className="hrpage__dim">전원</span>
                              : (
                                <span className={`hrpage__pill hrtr__scope${c.target === 0 ? ' is-empty' : ''}`}>
                                  {c.target === 0 ? '아직 안 고름' : `${c.target}명`}
                                </span>
                              )}
                            {c.outsiders > 0 && (
                              <span className="hrpage__dim hrtr__out"> 대상 밖 {c.outsiders}</span>
                            )}
                          </td>
                          <td className="hrpage__num">{c.done} / {c.target}</td>
                          <td className="hrpage__num">
                            <span className={`hrpage__pill hrtr__st is-${c.state.toLowerCase()}`}>
                              {c.state === 'DONE' ? STATE_LABEL.DONE : `${c.rate}%`}
                            </span>
                          </td>
                          <td className="hrpage__act">
                            <Button variant="secondary" size="sm" onClick={() => openRoster(c.id)} disabled={busy === c.id}>
                              이수 현황
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => openTargets(c.id, c.name)} disabled={busy === c.id}>
                              대상
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => archive(c.id, false)} disabled={busy === c.id}>
                              접기
                            </Button>
                            <Button variant="quiet-danger" size="sm" onClick={() => removeCourse(c.id)} disabled={busy === c.id}>
                              삭제
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  교육 열기
                  <span className="hrpage__panels">메일 · 알림 발송은 아직 없습니다 — 여는 것까지만 기록합니다</span>
                </h2>
                <div className="hrcert__form">
                  <label className="hrcert__field">
                    <span>교육</span>
                    <input
                      value={courseForm.name} maxLength={80} placeholder="예) 직장 내 성희롱 예방"
                      onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })}
                    />
                  </label>
                  <label className="hrcert__field">
                    <span>주기</span>
                    <select
                      value={courseForm.cycle}
                      onChange={(e) => setCourseForm({ ...courseForm, cycle: e.target.value as CourseCycle })}
                    >
                      {COURSE_CYCLES.map((c) => <option key={c} value={c}>{COURSE_CYCLE_LABEL[c]}</option>)}
                    </select>
                  </label>
                  <label className="hrcert__field">
                    <span>기한</span>
                    <input type="date" value={courseForm.dueOn} onChange={(e) => setCourseForm({ ...courseForm, dueOn: e.target.value })} />
                  </label>
                  <Button variant="primary" onClick={addCourse} disabled={busy === 'course'}>
                    {busy === 'course' ? '여는 중…' : '교육 열기'}
                  </Button>
                </div>
              </section>

              {targetsFor && (
                <section className="hrpage__panel">
                  <h2 className="hrpage__panelt">
                    {targetsFor.name} — 대상
                    <span className="hrpage__panels">
                      비워 두면 재직자 전원입니다 · 하나라도 고르면 그 사람들만 대상이 됩니다
                    </span>
                    <span className="hrtr__acts">
                      <Button variant="secondary" size="sm" onClick={() => { setTargetsFor(null); setTargets(null); }}>
                        닫기
                      </Button>
                    </span>
                  </h2>

                  <div className="hrcert__form">
                    <label className="hrcert__field">
                      {/* 탭 무리가 이미 «갈래» 라 이름을 나눈다 — 겹치면 무엇을 가리키는지 흐려진다. */}
                      <span>구분</span>
                      <select
                        value={pickKind}
                        onChange={(e) => { setPickKind(e.target.value as 'TEAM' | 'USER'); setPickId(''); }}
                      >
                        <option value="TEAM">팀</option>
                        <option value="USER">사람</option>
                      </select>
                    </label>
                    <label className="hrcert__field">
                      <span>{pickKind === 'TEAM' ? '팀' : '사람'}</span>
                      <select value={pickId} onChange={(e) => setPickId(e.target.value)}>
                        <option value="">{pickKind === 'TEAM' ? '팀 고르기' : '사람 고르기'}</option>
                        {pickKind === 'TEAM'
                          ? teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)
                          : employees.map((e) => (
                            <option key={e.userId} value={e.userId}>{e.name} · {e.teamName ?? '회사 직속'}</option>
                          ))}
                      </select>
                    </label>
                    <Button variant="primary" onClick={addTarget} disabled={busy === 'target'}>
                      {busy === 'target' ? '더하는 중…' : '대상에 더하기'}
                    </Button>
                  </div>

                  {!targets ? <p className="hrpage__empty">불러오는 중…</p>
                    : targets.length === 0 ? (
                      <p className="hrpage__empty">
                        고른 대상이 없습니다 — 지금은 이 교육의 대상이 <b>0명</b>입니다.
                        전원으로 되돌리려면 대상을 하나도 두지 말고 «전원» 으로 바꿔 주세요.
                      </p>
                    ) : (
                      <table className="hrpage__tbl">
                        <tbody>
                          {targets.map((t) => (
                            <tr key={t.id}>
                              <td className="hrpage__dim">{t.kind === 'TEAM' ? '팀' : '사람'}</td>
                              <td>{t.label}</td>
                              <td className="hrpage__act">
                                <Button variant="quiet-danger" size="sm" onClick={() => dropTarget(t.id)} disabled={busy === t.id}
                                >
                                  빼기
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                  <p className="hrpage__empty">
                    이미 이수한 사람이 대상에서 빠져도 <b>기록은 지우지 않습니다</b> — 실제로 들었기 때문입니다.
                    다만 이수율의 분자·분모 어느 쪽에도 넣지 않고 «대상 밖» 으로 따로 셉니다.
                  </p>
                </section>
              )}

              {roster && (
                <section className="hrpage__panel">
                  <h2 className="hrpage__panelt">
                    {roster.course.name} — 이수 현황
                    <span className="hrpage__panels">{roster.course.done} / {roster.course.target}명</span>
                    <span className="hrtr__acts">
                      <Button variant="secondary" size="sm" onClick={() => setRoster(null)}>닫기</Button>
                    </span>
                  </h2>
                  <table className="hrpage__tbl">
                    <thead>
                      <tr><th>직원</th><th>이수일</th><th className="hrpage__act">&nbsp;</th></tr>
                    </thead>
                    <tbody>
                      {roster.rows.map((r) => (
                        <tr key={r.userId} className={r.done ? '' : 'is-overdue'}>
                          <td>{r.name}<span className="hrpage__dim"> · {r.employeeNumber ?? '—'}</span></td>
                          <td className="hrpage__dim hrtr__nowrap">{r.completedOn ?? '미이수'}</td>
                          <td className="hrpage__act">
                            <button
                              type="button"
                              className={`hrtr__mark${r.done ? ' is-on' : ''}`}
                              onClick={() => toggleDone(roster.course.id, r.userId, !r.done)}
                              disabled={busy === `${roster.course.id}:${r.userId}`}
                              aria-pressed={r.done}
                              aria-label={`${r.name} ${r.done ? '이수' : '미이수'}`}
                            >
                              {r.done ? '이수' : '미이수'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {roster.outsiders.length > 0 && (
                    <p className="hrpage__empty">
                      대상은 아니지만 이수 기록이 있는 사람 {roster.outsiders.length}명 —{' '}
                      {roster.outsiders.map((r) => r.name).join(' · ')}. 기록은 그대로 둡니다.
                    </p>
                  )}
                </section>
              )}

              {board && board.archived.filter((c) => c.kind === tab).length > 0 && (
                <section className="hrpage__panel">
                  <h2 className="hrpage__panelt">
                    접어 둔 교육
                    <span className="hrpage__panels">지난 이수 기록이 가리키는 이름이라 지우지 않습니다</span>
                  </h2>
                  <table className="hrpage__tbl">
                    <tbody>
                      {board.archived.filter((c) => c.kind === tab).map((c) => (
                        <tr key={c.id}>
                          <td>{c.name}</td>
                          <td className="hrpage__dim hrpage__num">{c.done}명 이수</td>
                          <td className="hrpage__act">
                            <Button variant="secondary" size="sm" onClick={() => archive(c.id, true)} disabled={busy === c.id}>
                              다시 열기
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          ) : (
            <>
              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  {codes.labelOf('credential_kind', tab, CREDENTIAL_KIND_LABEL[tab] ?? tab)}
                  <span className="hrpage__panels">{credentials.length}건 · 만료 없는 것은 «—» 입니다</span>
                </h2>
                {credentials.length === 0 ? (
                  <p className="hrpage__empty">아직 등록된 기록이 없습니다.</p>
                ) : (
                  <table className="hrpage__tbl">
                    <thead>
                      <tr>
                        <th>직원</th><th>구분</th><th>이름</th><th>취득</th><th>만료</th><th>증빙</th>
                        <th className="hrpage__act">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credentials.map((c) => (
                        <tr key={c.id} className={c.expiry === 'EXPIRED' || c.expiry === 'SOON' ? 'is-overdue' : ''}>
                          <td>{c.userName ?? '—'}</td>
                          <td className="hrpage__dim">{codes.labelOf('credential_kind', c.kind, c.kindLabel)}</td>
                          <td>{c.title}</td>
                          <td className="hrpage__dim hrtr__nowrap">{c.acquiredOn}</td>
                          <td className="hrtr__nowrap">
                            {c.expiresOn ?? '—'}
                            {c.expiry !== 'NONE' && c.expiry !== 'VALID' && (
                              <span className={`hrpage__pill hrtr__d${c.expiry === 'EXPIRED' ? ' hrtr__d--past' : ''}`}>
                                {EXPIRY_LABEL[c.expiry]}
                              </span>
                            )}
                          </td>
                          <td className="hrpage__dim">
                            {c.hasProof ? (c.onCertificate ? '있음' : '만료') : '없음'}
                          </td>
                          <td className="hrpage__act">
                            <Button variant="quiet-danger" size="sm" onClick={() => removeCred(c)} disabled={busy === c.id}>
                              삭제
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="hrpage__panel">
                <h2 className="hrpage__panelt">
                  등록
                  <span className="hrpage__panels">만료가 없으면 만료일을 비워 둡니다 — 먼 날짜로 채우지 않습니다</span>
                </h2>
                <div className="hrcert__form">
                  <label className="hrcert__field">
                    <span>대상</span>
                    <select value={credForm.userId} onChange={(e) => setCredForm({ ...credForm, userId: e.target.value })}>
                      <option value="">사람 고르기</option>
                      {employees.map((e) => (
                        <option key={e.userId} value={e.userId}>{e.name} · {e.teamName ?? '회사 직속'}</option>
                      ))}
                    </select>
                  </label>
                  <label className="hrcert__field">
                    <span>이름</span>
                    <input
                      value={credForm.name} maxLength={80}
                      placeholder={tab === 'LANGUAGE' ? '예) TOEIC' : '예) 정보처리기사'}
                      onChange={(e) => setCredForm({ ...credForm, name: e.target.value })}
                    />
                  </label>
                  {tab === 'LANGUAGE' && (
                    <label className="hrcert__field">
                      <span>점수 · 급</span>
                      <input
                        value={credForm.score} maxLength={20} placeholder="예) 905"
                        onChange={(e) => setCredForm({ ...credForm, score: e.target.value })}
                      />
                    </label>
                  )}
                  <label className="hrcert__field">
                    <span>취득일</span>
                    <input type="date" value={credForm.acquiredOn} onChange={(e) => setCredForm({ ...credForm, acquiredOn: e.target.value })} />
                  </label>
                  <label className="hrcert__field">
                    <span>만료일</span>
                    <input type="date" value={credForm.expiresOn} onChange={(e) => setCredForm({ ...credForm, expiresOn: e.target.value })} />
                  </label>
                  <label className="hrcert__field hroff__check">
                    <input
                      type="checkbox" checked={credForm.hasProof}
                      onChange={(e) => setCredForm({ ...credForm, hasProof: e.target.checked })}
                    />
                    <span>증빙 있음</span>
                  </label>
                  <Button variant="primary" onClick={addCred} disabled={busy === 'cred'}>
                    {busy === 'cred' ? '저장 중…' : '저장'}
                  </Button>
                </div>
              </section>
            </>
          )}

          <div className="hrpage__note">
            이수 대상은 기본이 <b>재직자 전원</b>이고, 과정마다 «대상» 에서 팀 · 사람으로 좁힐 수 있습니다.
          </div>
        </>
      )}
    </div>
  );
}

function msgOf(e: unknown): string | null {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? null;
}
